import React, {useMemo} from "react";
import {Button, Divider, Grid, TextField, Typography} from "@mui/material";
import {useTranslation} from "react-i18next"
import {Auction} from "../../../../../../types/auction";
import {BidLot, BidLotDetailed, BidLotItem, LotsObject} from "../../../../../../types/bids";
import {SigningBundle} from "../../Bids";
import SignatureBtn from "../../../../../../common/SignatureBtn";
import {Dictionary} from "../../../../../../types/dictionary";
import {ThousandsSeparator} from "../../../../../../common/NumberFormat";

export function Rubric({
                           auction,
                           bidLots,
                           onItemPriceChange,
                           placeBid,
                           totalItemsPrices,
                           isOwned,
                           bidIdx,
                           signingBundle
                       }: {
    auction: Auction;
    bidLots?: BidLot[];
    onItemPriceChange?(lotIdx: number, idx: number, newCode: string, newPrice: string): void; // tender bids
    placeBid?(): Promise<void>; // tender bids
    totalItemsPrices?: number;
    isOwned?: boolean;
    bidIdx?: number;
    signingBundle?: SigningBundle
}) {
    const {t} = useTranslation();

    // Мемоизированная трансформация с правильной типизацией
    const transformLotsToObject = useMemo<LotsObject>(() => {

        const obj = (isOwned && bidIdx !== undefined ? auction.bids[bidIdx] : auction.bid)?.lots.reduce<LotsObject>((lotsAcc, lot: BidLotDetailed) => {
            return {
                ...lotsAcc,
                [lot.lotId]: {
                    ...lot,
                    items: lot.items.reduce<{ [itemId: string]: BidLotItem }>((itemsAcc, item) => {
                        return {
                            ...itemsAcc,
                            [item.itemId]: item
                        };
                    }, {})
                }
            };
        }, {}) || {};

        console.log(bidIdx, obj)

        return obj
    }, [auction.bid?.lots, bidIdx]);

    // Мемоизированная функция для получения элементов по lotId и itemId
    const getItemByIds = useMemo(() => {
        return (lotId: number, itemId: number) => {
            const lotIdStr = lotId.toString();
            const itemIdStr = itemId.toString();

            if (!transformLotsToObject[lotIdStr]) return null;
            return transformLotsToObject[lotIdStr].items[itemIdStr] || null;
        };
    }, [transformLotsToObject]);


// 3. Мемоизированная функция для получения списка всех лотов в виде массива
    const lotsList = useMemo(() => {
        return Object.values(transformLotsToObject);
    }, [transformLotsToObject]);

    return (
        <>
            {isOwned
                ? null
                : <Grid container columnSpacing={3} rowSpacing={1} sx={{mt: 1}}>
                    <Grid item xs={12} sx={{mb: 1}}>
                        <Typography variant="h6">
                            {t("auction.sections.rubric")}
                        </Typography>
                    </Grid>
                </Grid>
            }

            <Divider sx={{mt: isOwned ? 0 : 3, mb: isOwned ? 0 : 1, ml: "auto", mr: "auto", width: '85%'}}/>
            {auction.lots.map((lot, lotIdx, lots) => (
                <React.Fragment key={lotIdx}>
                    <Grid item xs={12} sx={{mb: 1}}>
                        <b>
                            {t("auction.fields.rubric.item")}
                            {lots.length > 1 ? ` #${lotIdx + 1}` : ""}
                        </b>
                    </Grid>
                    {lot.lotDto.lotItems.map((item, idx, items) => (
                        <React.Fragment key={idx}>
                            <Grid container columnSpacing={3} rowSpacing={1} sx={{mt: 1}}>
                                <Grid item xs={12} sm={6} md={4}>
                                    <TextField
                                        fullWidth color="primary" type="text" size="small"
                                        label={t("auction.dictionary.productType")}
                                        value={t(`product_type.${item.productType}`)}
                                        InputProps={{readOnly: true}}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <TextField
                                        fullWidth color="primary" type="text" size="small"
                                        label={t("auction.fields.rubric.productCode")}
                                        value={item.productCode}
                                        InputProps={{readOnly: true}}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={8}>
                                    <TextField
                                        fullWidth color="primary" type="text" size="small"
                                        label={t("auction.fields.rubric.productName")}
                                        value={item.productName}
                                        InputProps={{readOnly: true}}
                                    />
                                </Grid>
                                {isOwned
                                    ? null
                                    : <Grid item xs={12} sm={6} md={12} sx={{mt: 1}}>
                                        <TextField multiline minRows={4}
                                                   fullWidth color="primary" type="text" size="small"
                                                   label={t("auction.fields.rubric.productBrief")}
                                                   value={item.productBrief}
                                                   InputProps={{readOnly: true}}
                                        />
                                    </Grid>
                                }
                            </Grid>

                            <Grid container columnSpacing={3} rowSpacing={1} sx={{mt: isOwned ? 0 : 1}}>
                                <Grid item xs={12} sm={6} md={4}>
                                    <TextField
                                        fullWidth color="primary" type="text" size="small"
                                        label={t("auction.fields.rubric.price")}
                                        value={item.gross / item.amount}
                                        InputProps={{readOnly: true, inputComponent: ThousandsSeparator}}
                                        InputLabelProps={{shrink: true}}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <TextField
                                        fullWidth color="primary" type="text" size="small"
                                        label={t("auction.dictionary.unit")}
                                        value={item.unitName}
                                        InputProps={{readOnly: true}}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <TextField
                                        fullWidth color="primary" type="text" size="small"
                                        label={t("auction.fields.rubric.amount")}
                                        value={item.amount}
                                        InputProps={{readOnly: true, inputComponent: ThousandsSeparator}}
                                        InputLabelProps={{shrink: true}}
                                    />
                                </Grid>
                            </Grid>

                            {placeBid || isOwned
                                ? null
                                : <>
                                    {item.addresses.map((address, addressIdx, addresses) => (
                                        <Grid container columnSpacing={3} rowSpacing={1} sx={{mt: 1}}>
                                            <Grid item xs={12} sm={6} md={4}>
                                                <TextField fullWidth color="primary" size="small"
                                                           label={t("cabinet.new_plan_item.fields.address.region")}
                                                           value={address.region}
                                                           InputProps={{readOnly: true}}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={4}>
                                                <TextField fullWidth color="primary" size="small"
                                                           label={t("cabinet.new_plan_item.fields.address.district")}
                                                           value={address.district}
                                                           InputProps={{readOnly: true}}

                                                />
                                            </Grid>

                                            <Grid item xs={12} sm={6} md={4}>
                                                <TextField fullWidth color="primary" size="small"
                                                           label={t("cabinet.new_plan_item.fields.address.city")}
                                                           value={address.city}
                                                           InputProps={{readOnly: true}}

                                                />
                                            </Grid>

                                            <Grid item xs={12} sm={6} md={4}>
                                                <TextField fullWidth color="primary" size="small"
                                                           label={t("cabinet.new_plan_item.fields.address.village")}
                                                           value={address.village}
                                                           InputProps={{readOnly: true}}
                                                />
                                            </Grid>

                                            <Grid item xs={12} sm={6} md={4}>
                                                <TextField fullWidth color="primary" size="small"
                                                           label={t("cabinet.new_plan_item.fields.address.street")}
                                                           value={address.street}
                                                           InputProps={{readOnly: true}}
                                                />
                                            </Grid>

                                            <Grid item xs={12} sm={6} md={4}>
                                                <TextField fullWidth color="primary" size="small"
                                                           label={t("cabinet.new_plan_item.fields.address.building")}
                                                           value={address.building}
                                                           InputProps={{readOnly: true}}
                                                />
                                            </Grid>
                                        </Grid>
                                    ))}
                                </>
                            }

                            {placeBid || isOwned
                                ? <>
                                    {isOwned
                                        ? null
                                        : <Divider sx={{mt: 3, mb: 1, ml: "auto", mr: "auto", width: '98%'}}/>
                                    }

                                    <Grid container columnSpacing={3} rowSpacing={1} sx={{mt: 1}}>
                                        <Grid item xs={12}>

                                            <TextField
                                                fullWidth color="primary" type="number" size="small"
                                                label={isOwned
                                                    ? t("auction.fields.bid.offeredPrice")
                                                    : t("auction.fields.bid.yourPrice")
                                                }
                                                onChange={(e) => {
                                                    if (!onItemPriceChange) return;
                                                    onItemPriceChange(lotIdx, idx, item.productCode, e.target.value)
                                                }}
                                                value={
                                                    auction.isBidPlaced || isOwned
                                                        // ? isOwned && bidIdx !== undefined
                                                        //     ? auction.bids?.[bidIdx]?.lots[lotIdx]?.items?.[idx]?.price ?? ""
                                                        //     : auction.bid?.lots[lotIdx]?.items?.[idx]?.price ?? ""
                                                        ? transformLotsToObject[lot.id]?.items[item.id!]?.price ?? ""
                                                        : bidLots?.[lotIdx]?.items?.[idx]?.price ?? ""
                                                }


                                                InputProps={{
                                                    readOnly: auction.isBidPlaced || isOwned,
                                                    inputComponent: ThousandsSeparator
                                                }}
                                                onBlur={() => {
                                                    if (auction.isBidPlaced || isOwned) {
                                                        if (isOwned && bidIdx !== undefined) {
                                                            if (+transformLotsToObject[lot.id]?.items[item.id!]?.price === 0) {
                                                                if (!onItemPriceChange) return;
                                                                onItemPriceChange(lotIdx, idx, item.productCode, "")
                                                            }
                                                        } else if (!!bidLots && +bidLots[idx]?.items[0].price === 0) {
                                                            if (!onItemPriceChange) return;
                                                            onItemPriceChange(lotIdx, idx, item.productCode, "")
                                                        }
                                                    }
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                </>
                                : null
                            }
                        </React.Fragment>
                    ))}

                    <Grid container columnSpacing={3} rowSpacing={1} sx={{mt: 1}}>
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                fullWidth color="primary" type="text" size="small"
                                label={placeBid
                                    ? t("auction.fields.bid.startingGross")
                                    : t("auction.fields.rubric.gross")
                                }
                                value={lot.lotDto.lotItems.reduce(
                                    (accumulator, currentItem) => accumulator + currentItem.gross,
                                    0
                                )}
                                InputProps={{readOnly: true, inputComponent: ThousandsSeparator}}
                                InputLabelProps={{shrink: true}}
                            />
                        </Grid>

                        {placeBid || isOwned
                            ? <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    fullWidth color="primary" type="text" size="small"
                                    label={isOwned
                                        ? t("auction.fields.bid.offeredGross")
                                        : t("auction.fields.bid.yourGross")}
                                    value={isOwned
                                        ? auction.bids[bidIdx!].gross
                                        : totalItemsPrices ?? ""}
                                    InputProps={{readOnly: true, inputComponent: ThousandsSeparator}}
                                    InputLabelProps={{shrink: true}}
                                />
                            </Grid>
                            : null
                        }
                    </Grid>
                </React.Fragment>
            ))}

            < Divider sx={{mt: isOwned ? 0 : 3, mb: isOwned ? 0 : 1, ml: "auto", mr: "auto", width: '98%'}}/>
            <Grid container columnSpacing={3} rowSpacing={1} sx={{mt: 1}}>
                <Grid item xs={12} sm={6} md={4}>
                    <TextField
                        fullWidth color="primary" type="text" size="small"
                        label={placeBid
                            ? t("auction.fields.bid.startingGross")
                            : t("auction.fields.rubric.gross")
                        }
                        value={auction.gross}
                        InputProps={{readOnly: true, inputComponent: ThousandsSeparator}}
                        InputLabelProps={{shrink: true}}
                    />
                </Grid>
            </Grid>
            <Divider sx={{mt: isOwned ? 0 : 3, ml: "auto", width: '98%'}}/>

            {
                placeBid || isOwned
                    ? isOwned
                        ? null
                        : <Grid item container spacing={2} justifyContent="center" sx={{pt: 2}}>
                            <SignatureBtn
                                onSign={signingBundle?.onSign as () => Promise<void>}
                                isSigning={signingBundle?.isSigning as boolean}
                                signingError={signingBundle?.signingError as string}
                                storageType={signingBundle?.storageType as Dictionary}
                                signed={!!(signingBundle?.signature || auction.isBidPlaced)}
                            />

                            <Grid item xs="auto">
                                <Button fullWidth variant="contained" size="large"
                                        onClick={placeBid} color="primary"
                                        disabled={!signingBundle?.signature || auction.isBidPlaced}
                                >
                                    {auction.isBidPlaced
                                        ? t("auction.fields.bid.placed")
                                        : t("auction.fields.bid.place")
                                    }
                                </Button>
                            </Grid>
                        </Grid>

                    : <Grid container columnSpacing={3} rowSpacing={1} sx={{mt: 1}}>
                        <Grid item xs={12} sx={{mb: 1}}>
                            <Typography variant="h6">
                                {t("auction.sections.terms")}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                fullWidth color="primary" type="text" size="small"
                                label={t("auction.dictionary.deliveryCondition")}
                                value={t(`delivery_condition.${auction.deliveryCondition}`)}
                                InputProps={{readOnly: true}}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                fullWidth color="primary" type="text" size="small"
                                label={t("auction.fields.deliveryTime")}
                                value={auction.deliveryTime}
                                InputProps={{readOnly: true}}
                            />
                        </Grid>
                    </Grid>
            }
        </>
    )
}